import React, { createContext, useContext, useState, useEffect, type ReactNode } from 'react';
import type { TrainingClass, Agent, WorkPattern, ContractType, RAGStatus } from '../types';
import { TRAINING_WEEKS, HYPERCARE_DAYS, MANDATORY_COURSES } from '../lib/constants';
import { supabase } from '../lib/supabase';

export interface DailyLog {
  agentId: string;
  weekDay: string;
  challenges?: string;
  achievements?: string;
  systemIssues?: string;
  actionPlan?: string;
  isOnTrack?: boolean;
}

interface StoreContextType {
  classes: TrainingClass[];
  activeClassId: string | null;
  activeClass: TrainingClass | null;
  activeClassAgents: Agent[];
  userRole: string | null;
  isReadOnlyRole: boolean;
  setActiveClassId: (id: string) => void;
  addClass: (className: string, startDate: string) => void;
  deleteClass: (classId: string) => void;
  addAgent: (
    name: string,
    pNumber: string,
    email: string,
    options?: {
      accountNumber?: string;
      wprn?: string;
      c2mUsername?: string;
      c2mPassword?: string;
      personalPhone?: string;
      personalEmail?: string;
      password?: string;
      isIrishSpeaking?: boolean;
      isRejoiner?: boolean;
      workPattern?: WorkPattern;
      contractType?: ContractType;
    }
  ) => Promise<void>;
  updateAgent: (agentId: string, updates: Partial<Agent>) => Promise<void>;
  updateAgentAttendance: (agentId: string, date: string, present: boolean | undefined) => Promise<void>;
  updateAgentAssessment: (agentId: string, key: string, value: any) => Promise<void>;
  updateWeeklyEvaluation: (agentId: string, weekNumber: number, rag: RAGStatus, notes: string) => Promise<void>;
  updateCourseCompletion: (agentId: string, courseId: string, completed: boolean) => Promise<void>;
  updateHypercareLog: (...args: any[]) => Promise<void>;
  updateHypercare: (...args: any[]) => Promise<void>;
  deleteAgent: (agentId: string) => Promise<void>;
  getDailyLog: (agentId: string, weekDay: string) => DailyLog | undefined;
  upsertDailyLog: (log: DailyLog) => void;
}

const StoreContext = createContext<StoreContextType | undefined>(undefined);

const DAILY_LOGS_KEY = 'uisce_training_tracker_dailylogs_v4';

const defaultClass: TrainingClass = {
  id: 'class-1',
  className: 'Commercial Cohort',
  startDate: '2026-08-26',
  agents: []
};

export function StoreProvider({ children }: { children: ReactNode }) {
  const [classes, setClasses] = useState<TrainingClass[]>([defaultClass]);
  const [dailyLogs, setDailyLogs] = useState<DailyLog[]>(() => {
    const saved = localStorage.getItem(DAILY_LOGS_KEY);
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (e) {
        console.error('Failed to parse daily logs', e);
      }
    }
    return [];
  });

  const [activeClassId, setActiveClassId] = useState<string>('class-1');
  const userRole = localStorage.getItem('uisce-user-role');

  const isReadOnlyRole = userRole === 'viewer' || userRole === 'guest' || userRole === 'manager';

  // Fetch all staff, attendance, weekly RAG, assessments, hypercare logs, and mandatory courses from Supabase
  useEffect(() => {
    async function fetchStaffData() {
      const { data: staffData, error: staffError } = await supabase.from('onboarding_staff').select('*');
      const { data: attendanceData } = await supabase.from('onboarding_attendance').select('*');
      const { data: weeklyRagData } = await supabase.from('weekly_rag').select('*');
      const { data: assessmentsData } = await supabase.from('assessments').select('*');
      const { data: hypercareData } = await supabase.from('hypercare_logs').select('*');
      const { data: coursesData } = await supabase.from('mandatory_courses').select('*');

      if (staffError) {
        console.error('Error fetching staff from Supabase:', staffError.message);
        return;
      }

      if (staffData && staffData.length > 0) {
        const mappedAgents: Agent[] = staffData.map((row: any) => {
          const agentAttendance = (attendanceData || [])
            .filter((att: any) => att.staff_id === row.id)
            .map((att: any) => ({
              date: att.session_date,
              present: att.attended
            }));

          const agentWeeklyEvaluations = Array.from({ length: TRAINING_WEEKS }, (_, i) => {
            const weekNum = i + 1;
            const existingRag = (weeklyRagData || []).find(
              (rag: any) => rag.staff_id === row.id && Number(rag.week_number) === weekNum
            );
            return {
              weekNumber: weekNum,
              calculatedRAG: (existingRag?.rag_status as RAGStatus) || 'Not Rated',
              notes: existingRag?.notes || ''
            };
          });

          const staffAssessments = (assessmentsData || []).filter((a: any) => a.staff_id === row.id);
          const getScore = (name: string) => staffAssessments.find((a: any) => a.assessment_name === name)?.score ?? '';
          const getStatus = (name: string) => staffAssessments.find((a: any) => a.assessment_name === name)?.status ?? 'Pending';

          const agentHypercareLogs = Array.from({ length: HYPERCARE_DAYS }, (_, i) => {
            const dayNum = i + 1;
            const existingHc = (hypercareData || []).find(
              (hc: any) => hc.staff_id === row.id && Number(hc.day_number) === dayNum
            );
            return {
              dayNumber: dayNum,
              callsTaken: existingHc?.calls_taken || 0,
              rag: (existingHc?.rag_status as RAGStatus) || 'Not Rated',
              notes: existingHc?.notes || ''
            };
          });

          const staffCourses = (coursesData || []).filter((c: any) => c.staff_id === row.id);
          const agentCourses = MANDATORY_COURSES.map((c) => {
            const found = staffCourses.find((sc: any) => sc.course_id === c.id);
            return {
              courseId: c.id,
              completed: found ? Boolean(found.completed) : false,
              completionDate: found?.completion_date || undefined
            };
          });

          return {
            id: row.id,
            name: row.name || '',
            pNumber: row.p_number || '',
            email: row.email || '',
            accountNumber: row.account_number || '',
            wprn: row.wprn || '',
            c2mUsername: row.c2m_username || '',
            c2mPassword: row.c2m_password || '',
            personalPhone: row.personal_phone || '',
            personalEmail: row.personal_email || '',
            password: row.pin || '1234',
            isIrishSpeaking: row.is_irish_speaking ?? false,
            isRejoiner: row.is_rejoiner ?? false,
            workPattern: row.work_pattern ?? 'Hybrid',
            contractType: row.contract_type ?? 'Full-time',
            attendance: agentAttendance,
            assessments: {
              directDebit: getScore('directDebit'),
              customerContact: getScore('customerContact'),
              meterLocation: getScore('meterLocation'),
              moveInOut: getScore('moveInOut'),
              meterReading: getScore('meterReading'),
              cruComplaints: getScore('cruComplaints'),
              writtenAttempt1: getScore('writtenAttempt1'),
              writtenAttempt2: getScore('writtenAttempt2'),
              callAccreditation: {
                attempt1: getScore('callAttempt1'),
                attempt2: getScore('callAttempt2'),
                attempt3: getScore('callAttempt3'),
                finalStatus: getStatus('finalAccreditation')
              }
            },
            weeklyEvaluations: agentWeeklyEvaluations,
            courses: agentCourses,
            hypercareLogs: agentHypercareLogs
          };
        });

        setClasses([{ ...defaultClass, agents: mappedAgents }]);
      }
    }

    fetchStaffData();
  }, []);

  useEffect(() => {
    localStorage.setItem(DAILY_LOGS_KEY, JSON.stringify(dailyLogs));
  }, [dailyLogs]);

  const activeClass = classes.find((c) => c.id === activeClassId) || classes[0] || null;
  const activeClassAgents = activeClass ? activeClass.agents : [];

  const addClass = (className: string, startDate: string) => {
    if (isReadOnlyRole) return alert('Read-only access: Cannot add cohort.');
    const newClass: TrainingClass = {
      id: `class-${Date.now()}`,
      className,
      startDate,
      agents: []
    };
    setClasses((prev) => [...prev, newClass]);
    setActiveClassId(newClass.id);
  };

  const deleteClass = (classId: string) => {
    if (isReadOnlyRole) return alert('Read-only access: Cannot delete cohort.');
    setClasses((prev) => {
      const filtered = prev.filter((c) => c.id !== classId);
      if (activeClassId === classId && filtered.length > 0) {
        setActiveClassId(filtered[0].id);
      }
      return filtered;
    });
  };

  const addAgent = async (
    name: string,
    pNumber: string,
    email: string,
    options?: {
      accountNumber?: string;
      wprn?: string;
      c2mUsername?: string;
      c2mPassword?: string;
      personalPhone?: string;
      personalEmail?: string;
      password?: string;
      isIrishSpeaking?: boolean;
      isRejoiner?: boolean;
      workPattern?: WorkPattern;
      contractType?: ContractType;
    }
  ) => {
    if (isReadOnlyRole) return alert('Read-only access: Cannot add trainees.');
    if (!activeClass) return;

    const generatedPin = Math.floor(1000 + Math.random() * 9000).toString();
    const formattedPNumber = pNumber.toUpperCase();
    const finalPassword = options?.password || generatedPin;

    const { data, error } = await supabase
      .from('onboarding_staff')
      .insert([
        {
          name,
          p_number: formattedPNumber,
          email,
          pin: finalPassword,
          account_number: options?.accountNumber || null,
          wprn: options?.wprn || null,
          c2m_username: options?.c2mUsername || null,
          c2m_password: options?.c2mPassword || null,
          personal_phone: options?.personalPhone || null,
          personal_email: options?.personalEmail || null,
          work_pattern: options?.workPattern ?? 'Hybrid',
          contract_type: options?.contractType ?? 'Full-time',
          is_irish_speaking: options?.isIrishSpeaking ?? false,
          is_rejoiner: options?.isRejoiner ?? false
        }
      ])
      .select();

    if (error || !data || data.length === 0) {
      console.error('Error inserting agent to Supabase:', error);
      alert('Failed to save trainee to Supabase: ' + (error?.message || 'No data returned'));
      return;
    }

    const createdStaffRow = data[0];

    const newAgent: Agent = {
      id: createdStaffRow.id,
      name,
      pNumber: formattedPNumber,
      email,
      accountNumber: options?.accountNumber || '',
      wprn: options?.wprn || '',
      c2mUsername: options?.c2mUsername || '',
      c2mPassword: options?.c2mPassword || '',
      personalPhone: options?.personalPhone || '',
      personalEmail: options?.personalEmail || '',
      password: finalPassword,
      isIrishSpeaking: options?.isIrishSpeaking ?? false,
      isRejoiner: options?.isRejoiner ?? false,
      workPattern: options?.workPattern ?? 'Hybrid',
      contractType: options?.contractType ?? 'Full-time',
      attendance: [],
      assessments: {
        callAccreditation: { attempt1: '', attempt2: '', attempt3: '', finalStatus: 'Pending' }
      },
      weeklyEvaluations: Array.from({ length: TRAINING_WEEKS }, (_, i) => ({
        weekNumber: i + 1,
        calculatedRAG: 'Not Rated',
        notes: ''
      })),
      courses: MANDATORY_COURSES.map((c) => ({ courseId: c.id, completed: false })),
      hypercareLogs: Array.from({ length: HYPERCARE_DAYS }, (_, i) => ({
        dayNumber: i + 1,
        callsTaken: 0,
        rag: 'Not Rated',
        notes: ''
      }))
    };

    setClasses((prev) =>
      prev.map((c) => {
        if (c.id !== activeClass.id) return c;
        return { ...c, agents: [...c.agents, newAgent] };
      })
    );
  };

  const updateAgent = async (agentId: string, updates: Partial<Agent>) => {
    if (isReadOnlyRole) return alert('Read-only access: Cannot update trainee details.');
    if (!activeClass) return;

    setClasses((prevClasses) =>
      prevClasses.map((c) => {
        if (c.id !== activeClass.id) return c;
        return {
          ...c,
          agents: c.agents.map((ag) =>
            ag.id === agentId ? { ...ag, ...updates } : ag
          )
        };
      })
    );

    const payload: Record<string, any> = {};
    if (updates.name !== undefined) payload.name = updates.name;
    if (updates.pNumber !== undefined) payload.p_number = updates.pNumber.toUpperCase();
    if (updates.email !== undefined) payload.email = updates.email;
    if (updates.accountNumber !== undefined) payload.account_number = updates.accountNumber;
    if (updates.wprn !== undefined) payload.wprn = updates.wprn;
    if (updates.c2mUsername !== undefined) payload.c2m_username = updates.c2mUsername;
    if (updates.c2mPassword !== undefined) payload.c2m_password = updates.c2mPassword;
    if (updates.personalPhone !== undefined) payload.personal_phone = updates.personalPhone;
    if (updates.personalEmail !== undefined) payload.personal_email = updates.personalEmail;
    if (updates.password !== undefined) payload.pin = updates.password;
    if (updates.workPattern !== undefined) payload.work_pattern = updates.workPattern;
    if (updates.contractType !== undefined) payload.contract_type = updates.contractType;

    if (Object.keys(payload).length > 0) {
      const { error } = await supabase
        .from('onboarding_staff')
        .update(payload)
        .eq('id', agentId);

      if (error) {
        console.error('Error updating agent in Supabase:', error);
        alert('Failed to save changes to database: ' + error.message);
      }
    }
  };

  const updateAgentAttendance = async (agentId: string, date: string, present: boolean | undefined) => {
    if (isReadOnlyRole) return alert('Read-only access: Cannot modify attendance.');
    if (!activeClass) return;

    setClasses((prevClasses) =>
      prevClasses.map((c) => {
        if (c.id !== activeClass.id) return c;
        return {
          ...c,
          agents: c.agents.map((ag) => {
            if (ag.id !== agentId) return ag;
            const existingIndex = ag.attendance.findIndex((a) => a.date === date);
            let newAttendance = [...ag.attendance];
            
            if (present === undefined) {
              if (existingIndex >= 0) newAttendance.splice(existingIndex, 1);
            } else {
              if (existingIndex >= 0) {
                newAttendance[existingIndex] = { ...newAttendance[existingIndex], present };
              } else {
                newAttendance.push({ date, present });
              }
            }
            return { ...ag, attendance: newAttendance };
          })
        };
      })
    );

    if (present === undefined) {
      const { error } = await supabase
        .from('onboarding_attendance')
        .delete()
        .match({ staff_id: agentId, session_date: date });

      if (error) {
        console.error('Delete Attendance Error:', error);
        alert('Attendance delete failed: ' + error.message);
      }
    } else {
      const { error } = await supabase
        .from('onboarding_attendance')
        .upsert(
          { 
            staff_id: agentId, 
            session_date: date, 
            attended: present,
            session_title: 'Daily Attendance' 
          },
          { onConflict: 'staff_id,session_date' }
        );

      if (error) {
        console.error('Upsert Attendance Error:', error);
        alert('Attendance save failed: ' + error.message);
      }
    }
  };

  const updateAgentAssessment = async (agentId: string, key: string, value: any) => {
    if (isReadOnlyRole) return alert('Read-only access: Cannot modify assessment scores.');
    if (!activeClass) return;

    setClasses((prev) =>
      prev.map((c) => {
        if (c.id !== activeClass.id) return c;
        return {
          ...c,
          agents: c.agents.map((ag) => {
            if (ag.id !== agentId) return ag;
            if (key.startsWith('callAccreditation.')) {
              const subKey = key.split('.')[1];
              return {
                ...ag,
                assessments: {
                  ...ag.assessments,
                  callAccreditation: {
                    ...ag.assessments?.callAccreditation,
                    [subKey]: value
                  }
                }
              };
            }
            return {
              ...ag,
              assessments: {
                ...ag.assessments,
                [key]: value
              }
            };
          })
        };
      })
    );

    let assessmentName = key;
    
    // Explicitly parse empty strings, null, or undefined as null so clearing inputs saves null instead of 0
    let numericScore: number | null = null;
    if (value !== '' && value !== null && value !== undefined && !isNaN(Number(value))) {
      numericScore = Number(value);
    }
    
    let statusValue = 'Completed';

    if (key.startsWith('callAccreditation.')) {
      const subKey = key.split('.')[1];
      if (subKey === 'finalStatus') {
        assessmentName = 'finalAccreditation';
        numericScore = null;
        statusValue = value;
      } else {
        assessmentName = `call${subKey.charAt(0).toUpperCase() + subKey.slice(1)}`;
      }
    }

    const { error } = await supabase
      .from('assessments')
      .upsert(
        {
          staff_id: agentId,
          assessment_name: assessmentName,
          score: numericScore,
          status: statusValue
        },
        { onConflict: 'staff_id,assessment_name' }
      );

    if (error) {
      console.error('Error saving assessment score to Supabase:', error);
      alert('Failed to save assessment score: ' + error.message);
    }
  };

  const updateWeeklyEvaluation = async (agentId: string, weekNumber: number, rag: RAGStatus, notes: string) => {
    if (isReadOnlyRole) return alert('Read-only access: Cannot modify Weekly RAG details.');
    if (!activeClass) return;

    setClasses((prev) =>
      prev.map((c) => {
        if (c.id !== activeClass.id) return c;
        return {
          ...c,
          agents: c.agents.map((ag) => {
            if (ag.id !== agentId) return ag;
            const existingIndex = ag.weeklyEvaluations.findIndex((e) => e.weekNumber === weekNumber);
            let updatedEvals = [...ag.weeklyEvaluations];
            if (existingIndex >= 0) {
              updatedEvals[existingIndex] = { weekNumber, calculatedRAG: rag, notes };
            } else {
              updatedEvals.push({ weekNumber, calculatedRAG: rag, notes });
            }
            return { ...ag, weeklyEvaluations: updatedEvals };
          })
        };
      })
    );

    const { error } = await supabase
      .from('weekly_rag')
      .upsert(
        {
          staff_id: agentId,
          week_number: weekNumber,
          rag_status: rag,
          notes: notes
        },
        { onConflict: 'staff_id,week_number' }
      );

    if (error) {
      console.error('Error saving Weekly RAG to Supabase:', error);
      alert('Failed to save Weekly RAG evaluation to database: ' + error.message);
    }
  };

  const updateCourseCompletion = async (agentId: string, courseId: string, completed: boolean) => {
    if (isReadOnlyRole) return alert('Read-only access: Cannot update course completion.');
    if (!activeClass) return;

    const compDate = completed ? new Date().toISOString().slice(0, 10) : null;

    setClasses((prev) =>
      prev.map((c) => {
        if (c.id !== activeClass.id) return c;
        return {
          ...c,
          agents: c.agents.map((ag) => {
            if (ag.id !== agentId) return ag;
            let courses = ag.courses ? [...ag.courses] : [];
            const courseIndex = courses.findIndex((co) => co.courseId === courseId);
            
            if (courseIndex >= 0) {
              courses[courseIndex] = {
                ...courses[courseIndex],
                completed,
                completionDate: completed ? compDate || undefined : undefined
              };
            } else {
              courses.push({
                courseId,
                completed,
                completionDate: completed ? compDate || undefined : undefined
              });
            }
            return { ...ag, courses };
          })
        };
      })
    );

    const { error } = await supabase
      .from('mandatory_courses')
      .upsert(
        {
          staff_id: agentId,
          course_id: courseId,
          completed: completed,
          completion_date: compDate
        },
        { onConflict: 'staff_id,course_id' }
      );

    if (error) {
      console.error('Error saving course completion to Supabase:', error);
      alert('Failed to save course completion to database: ' + error.message);
    }
  };

  const updateHypercareLog = async (...args: any[]) => {
    if (isReadOnlyRole) return alert('Read-only access: Cannot modify hypercare logs.');
    if (!activeClass || activeClassAgents.length === 0) return;

    let targetAgentId = activeClassAgents[0].id;
    let dayNumber = 1;
    let callsTaken = 0;
    let rag: RAGStatus = 'Not Rated';
    let notes = '';

    if (args.length >= 5) {
      targetAgentId = args[0];
      dayNumber = Number(args[1]) || 1;
      callsTaken = args[2];
      rag = args[3] || 'Not Rated';
      notes = args[4] || '';
    } else if (args.length === 4) {
      if (typeof args[0] === 'string' && args[0].length > 10) {
        targetAgentId = args[0];
        dayNumber = Number(args[1]) || 1;
        callsTaken = args[2];
        rag = args[3] || 'Not Rated';
      } else {
        dayNumber = Number(args[0]) || 1;
        callsTaken = args[1];
        rag = args[2] || 'Not Rated';
        notes = args[3] || '';
      }
    } else if (args.length === 3) {
      dayNumber = Number(args[0]) || 1;
      callsTaken = args[1];
      rag = args[2] || 'Not Rated';
    } else if (args.length === 2) {
      dayNumber = Number(args[0]) || 1;
      callsTaken = args[1];
    }

    let cleanCallsTaken = 0;
    if (typeof callsTaken === 'object' && callsTaken !== null) {
      cleanCallsTaken = Number(callsTaken.callsTaken ?? callsTaken.target?.value ?? 0) || 0;
    } else {
      cleanCallsTaken = Number(callsTaken) || 0;
    }

    setClasses((prev) =>
      prev.map((c) => {
        if (c.id !== activeClass.id) return c;
        return {
          ...c,
          agents: c.agents.map((ag) => {
            if (ag.id !== targetAgentId) return ag;
            const updatedLogs = ag.hypercareLogs.map((hl) =>
              hl.dayNumber === dayNumber ? { ...hl, callsTaken: cleanCallsTaken, rag, notes } : hl
            );
            return { ...ag, hypercareLogs: updatedLogs };
          })
        };
      })
    );

    const { error } = await supabase
      .from('hypercare_logs')
      .upsert(
        {
          staff_id: targetAgentId,
          day_number: Number(dayNumber),
          calls_taken: cleanCallsTaken,
          rag_status: rag,
          notes: notes
        },
        { onConflict: 'staff_id,day_number' }
      );

    if (error) {
      console.error('Error saving Hypercare log to Supabase:', error);
      alert('Failed to save Hypercare log to database: ' + error.message);
    }
  };

  const deleteAgent = async (agentId: string) => {
    if (isReadOnlyRole) return alert('Read-only access: Cannot delete trainees.');
    if (!activeClass) return;

    const { error } = await supabase
      .from('onboarding_staff')
      .delete()
      .eq('id', agentId);

    if (error) {
      console.error('Error deleting agent in Supabase:', error);
      alert('Failed to delete trainee from database: ' + error.message);
      return;
    }

    setClasses((prev) =>
      prev.map((c) => {
        if (c.id !== activeClass.id) return c;
        return {
          ...c,
          agents: c.agents.filter((ag) => ag.id !== agentId)
        };
      })
    );
  };

  const getDailyLog = (agentId: string, weekDay: string) => {
    return dailyLogs.find((l) => l.agentId === agentId && l.weekDay === weekDay);
  };

  const upsertDailyLog = (log: DailyLog) => {
    if (isReadOnlyRole) return alert('Read-only access: Cannot modify daily logs.');
    setDailyLogs((prev) => {
      const index = prev.findIndex((l) => l.agentId === log.agentId && l.weekDay === log.weekDay);
      if (index >= 0) {
        const updated = [...prev];
        updated[index] = log;
        return updated;
      }
      return [...prev, log];
    });
  };

  return (
    <StoreContext.Provider
      value={{
        classes,
        activeClassId,
        activeClass,
        activeClassAgents,
        userRole,
        isReadOnlyRole,
        setActiveClassId,
        addClass,
        deleteClass,
        addAgent,
        updateAgent,
        updateAgentAttendance,
        updateAgentAssessment,
        updateWeeklyEvaluation,
        updateCourseCompletion,
        updateHypercareLog,
        updateHypercare: updateHypercareLog,
        deleteAgent,
        getDailyLog,
        upsertDailyLog
      }}
    >
      {children}
    </StoreContext.Provider>
  );
}

export function useStore() {
  const context = useContext(StoreContext);
  if (!context) throw new Error('useStore must be used within a StoreProvider');
  return context;
}